import React from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

/**
 * Formatters and small presentational pieces shared by the two condensed report
 * pages: /all/investment/overview and /all/investment/detail.
 *
 * COPIED FROM InvestorReport.js, NOT EXTRACTED FROM IT. That file keeps its own
 * definitions and was not touched, because /investor-report,
 * /investment-portfolio and -v1 all render through it and were asked to keep
 * working exactly as they do - and removing these from there, however
 * mechanical, is still a change to them.
 *
 * So this is duplication, deliberately, and worth stating plainly rather than
 * discovering later: these are pure formatters plus two stateless components. If
 * `money()` ever needs changing it needs changing in both, and a divergence
 * would be visible on screen immediately rather than hiding inside a figure -
 * which is the only kind of duplication that stays honest. The alternatives were
 * touching the frozen pages, or hand-retyping table markup, and both are worse.
 *
 * The bodies were copied programmatically, not retyped, so they are identical to
 * the originals character for character.
 */

export const abs = (v, dp = 2) =>
  Math.abs(v || 0).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

export const sym = (ccy) => ({ USD: "$", EUR: "€", GBP: "£", JPY: "¥" }[ccy] || `${ccy} `);

export const money = (v, ccy, dp = 2) => `${(v || 0) < 0 ? "−" : ""}${sym(ccy)}${abs(v, dp)}`;

export const signed = (v, ccy, dp = 2) => `${(v || 0) < 0 ? "−" : "+"}${sym(ccy)}${abs(v, dp)}`;

export const compact = (v, ccy) => {
  const a = Math.abs(v || 0);
  const s = (v || 0) < 0 ? "−" : "";
  if (a >= 1e6) return `${s}${sym(ccy)}${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}${sym(ccy)}${(a / 1e3).toFixed(1)}K`;
  return `${s}${sym(ccy)}${a.toFixed(0)}`;
};

export const pctStr = (v, dp = 2) => `${(v || 0) < 0 ? "−" : "+"}${Math.abs(v || 0).toFixed(dp)}%`;

export const plain = (v, dp = 1) => `${(v || 0).toFixed(dp)}%`;

export const dir = (v) => (v > 0 ? "up" : v < 0 ? "down" : "flat");

export const sumBy = (rows, key) => (rows || []).reduce((a, r) => a + (r[key] || 0), 0);

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function tzLabel(minutes, label) {
  if (label) return label;
  const m = minutes || 0;
  if (m === 330) return "IST";
  const sign = m < 0 ? "-" : "+";
  const h = Math.floor(Math.abs(m) / 60);
  const mm = Math.abs(m) % 60;
  return `GMT${sign}${h}${mm ? ":" + String(mm).padStart(2, "0") : ""}`;
}

export function serverTime(unix) {
  if (!unix) return { date: "—", time: "" };
  const d = new Date(unix * 1000);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const yy = String(d.getUTCFullYear()).slice(2);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mi = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return { date: `${dd} ${MONTHS[d.getUTCMonth()]} ${yy}`, time: `${hh}:${mi}:${ss}` };
}

export function Delta({ value, dp = 2 }) {
  const Icon = (value || 0) >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`ir-delta ir-${dir(value)}`}>
      <Icon size={13} strokeWidth={2.5} aria-hidden="true" />
      {pctStr(value, dp)}
    </span>
  );
}

export function DateTime({ unix }) {
  const { date, time } = serverTime(unix);
  return (
    <span className="ir-dt">
      <span className="ir-dt-d">{date}</span>
      <span className="ir-dt-t">{time}</span>
    </span>
  );
}

export const TABLE_COLS = ["5%", "13.3%", "8.55%", "8.55%", "13.3%", "13.3%", "10.9%", "11.9%", "15.2%"];

export const TABLE_COL_COUNT = TABLE_COLS.length;

export const ColGroup = () => (
  <colgroup>
    {TABLE_COLS.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
);

export function FillerRows({ count, cols }) {
  if (count <= 0) return null;
  return Array.from({ length: count }, (_, i) => (
    <tr className="ir-filler" key={`f${i}`} aria-hidden="true">
      {/* Empty on a filler row: numbering a row that is not there would
          make the two panels' visible counts disagree with their logs. */}
      <td className="ir-idx" />
      <td>
        <span className="ir-sym">&nbsp;</span>
        <span className="ir-ticket">&nbsp;</span>
      </td>
      <td colSpan={cols - 2} />
    </tr>
  ));
}
